import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Star, Check, RefreshCw, Loader2, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURES = ['all', 'cat_tool', 'mtpe_pro', 'proofread', 'tqa', 'smart_review'];
const FEATURE_LABELS = {
  cat_tool: 'CAT Tool', mtpe_pro: 'MTPE Pro', proofread: 'Proofread',
  tqa: 'TQA', smart_review: 'Smart Review'
};

const StarDisplay = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(s => (
      <Star key={s} size={14} className={s <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'} />
    ))}
  </div>
);

export default function ExportFeedbackAdmin({ adminToken }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [featureFilter, setFeatureFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [markingId, setMarkingId] = useState(null);

  const axiosConfig = { headers: { Authorization: `Bearer ${adminToken}` } };

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (featureFilter !== 'all') params.append('feature', featureFilter);
      if (ratingFilter !== 'all') params.append('rating', ratingFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await axios.get(`${API}/api/admin/feedback/export?${params}`, axiosConfig);
      setItems(res.data.feedback || []);
      setStats(res.data.stats || null);
    } catch {
      toast.error('Failed to fetch export feedback');
    } finally {
      setLoading(false);
    }
  }, [featureFilter, ratingFilter, statusFilter, adminToken]);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  const markReviewed = async (id) => {
    setMarkingId(id);
    try {
      await axios.put(`${API}/api/admin/feedback/export/${id}/review`, {}, axiosConfig);
      setItems(prev => prev.map(i => i.id === id ? { ...i, status: 'reviewed' } : i));
      toast.success('Marked as reviewed');
    } catch {
      toast.error('Failed to update');
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-500 mt-1">Total responses</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.avg_rating || '—'}</p>
            <p className="text-xs text-gray-500 mt-1">Avg rating</p>
          </Card>
          {Object.entries(stats.by_feature || {}).slice(0, 2).map(([feat, count]) => (
            <Card key={feat} className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{count}</p>
              <p className="text-xs text-gray-500 mt-1">{FEATURE_LABELS[feat] || feat}</p>
            </Card>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <Filter size={16} className="text-gray-400" />
        <select value={featureFilter} onChange={e => setFeatureFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          {FEATURES.map(f => <option key={f} value={f}>{f === 'all' ? 'All features' : FEATURE_LABELS[f] || f}</option>)}
        </select>
        <select value={ratingFilter} onChange={e => setRatingFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All ratings</option>
          {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} stars</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="reviewed">Reviewed</option>
        </select>
        <Button variant="outline" size="sm" onClick={fetchFeedback}>
          <RefreshCw size={14} className="mr-1" />Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-blue-600" size={28} />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">No export feedback yet.</Card>
      ) : (
        <Card>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Feature</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <React.Fragment key={item.id}>
                  <tr
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  >
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{item.user_email}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {FEATURE_LABELS[item.feature] || item.feature}
                      </span>
                    </td>
                    <td className="px-4 py-3"><StarDisplay rating={item.rating} /></td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        item.status === 'reviewed' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {item.status === 'reviewed' ? 'Reviewed' : 'New'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {item.status === 'new' && (
                        <Button
                          variant="outline" size="sm"
                          disabled={markingId === item.id}
                          onClick={(e) => { e.stopPropagation(); markReviewed(item.id); }}
                        >
                          {markingId === item.id
                            ? <Loader2 size={12} className="animate-spin" />
                            : <><Check size={12} className="mr-1" />Mark reviewed</>
                          }
                        </Button>
                      )}
                    </td>
                  </tr>
                  {expandedId === item.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={6} className="px-6 py-4 space-y-3">
                        {item.comment && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Comment</p>
                            <p className="text-sm text-gray-700">{item.comment}</p>
                          </div>
                        )}
                        {item.screenshot_base64 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Screenshot</p>
                            <img
                              src={item.screenshot_base64}
                              alt="feedback screenshot"
                              className="max-w-md rounded-lg border border-gray-200"
                            />
                          </div>
                        )}
                        {!item.comment && !item.screenshot_base64 && (
                          <p className="text-sm text-gray-400 italic">No comment or screenshot provided.</p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
